import { supabase } from "~/integrations/supabase/client";
import { SalesRequest } from "~/types/client";

export interface Seller {
  id: string;
  name: string;
  lastname: string;
  email: string;
}

export interface SellerClient {
  id: string;
  name: string;
  email: string | null;
  tax_id: string | null;
  insured_amount: number | null;
  trailing_payment_days: number | null;
  created_at: string;
}

export const fetchSellers = async (): Promise<Seller[]> => {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, name, lastname, email")
      .eq("role", "seller");

    if (error) {
      console.error("Error fetching sellers:", error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error("Failed to fetch sellers:", error);
    return [];
  }
};

export const fetchSellerDetails = async (sellerId: string) => {
  try {
    // Fetch sales requests assigned to this seller
    const { data: salesRequestsData, error: salesError } = await supabase
      .from("sales_request")
      .select("*")
      .eq("created_by", sellerId);

    if (salesError) throw salesError;

    // Fetch clients assigned to this seller
    const { data: clientsData, error: clientsError } = await supabase
      .from("clients")
      .select("*")
      .eq("assigned_seller", sellerId);

    if (clientsError) throw clientsError;

    // Transform sales requests to match the SalesRequest interface
    const salesRequests: SalesRequest[] = (salesRequestsData || []).map(
      (request) => ({
        id: request.id,
        clientId: request.client_id,
        createdBy: request.created_by,
        approvedRejectedBy: request.approved_rejected_by || undefined,
        status: request.status,
        paid: request.paid,
        paymentTerms: request.payment_terms,
        paymentType: request.payment_type as "future" | "prepaid",
        paymentDueDate: request.payment_due_date,
        totalAmount: request.total_amount,
        comments: request.comments || undefined,
        deliveryDate: request.delivery_date || undefined,
        deliveryAddressId: request.delivery_address_id || undefined,
        // Direct address fields
        deliveryStreet: request.delivery_street || undefined,
        deliveryNumber: request.delivery_number || undefined,
        deliveryComplement: request.delivery_complement || undefined,
        deliveryCity: request.delivery_city || undefined,
        deliveryState: request.delivery_state || undefined,
        deliveryComuna: request.delivery_comuna || undefined,
        rejectedReason: request.rejected_reason || undefined,
        createdAt: request.created_at,
        updatedAt: request.updated_at,
      })
    );

    return {
      salesRequests,
      clients: clientsData || [],
    };
  } catch (error) {
    console.error("Error fetching seller details:", error);
    return {
      salesRequests: [],
      clients: [],
    };
  }
};

// Helper function to map status from database to client interface
const mapStatusToClientType = (
  status: string
): "pending" | "accepted" | "rejected" => {
  switch (status) {
    case "pending_approval":
      return "pending";
    case "approved":
      return "accepted";
    case "rejected":
      return "rejected";
    default:
      return "pending"; // Default fallback
  }
};
